const LOG_PREFIX = '[OCM][HB] '

module.exports = class HeaderBidding {
    utils
    config
    hb_config
    mode
    ratio
    screen_width
    screen_height
    timeout
    analytics_adapters = []

    constructor(utils, config) {
        this.utils = utils
        this.config = config
        this.hb_config = config.services.header_bidding

        this.mode = this.hb_config.functionality
        if (this.mode === 'lazyload_v2' && this.utils.browser.isSafari) {
            this.mode = 'lazyload_v1'
        }

        this.utils.window.OCM.hb_mode = this.mode

        this.ratio = 1
        this.timeout = parseInt(this.hb_config.bidderTimeout)

        this.utils.window.ocmpbjs = this.utils.window.ocmpbjs || {que: []}

        this.loadPrebidLib()
    }

    async prepare(isLazyLoad) {
        isLazyLoad = (typeof isLazyLoad === 'undefined') ? false : isLazyLoad

        if (this.timeout === 1) {
            if (this.config.debug || this.hb_config.debug) {
                console.log(LOG_PREFIX + 'Timeout was set to 1ms, terminating process.')
            }

            return
        }

        // Preparation
        this.adjustBidderTimeout(isLazyLoad)

        this.setupIdHub()

        // this.loadRti()

        if (this.hb_config.analytics.includes('rivr')) {
            this.loadRivr()
        }

        // await this.filterAdUnitsByDataAttribute()
        await this.filterAdUnitsByPageType()
        await this.filterAdUnitsByDevice()
        await this.jsonifyAds()
        await this.removeNullBidParameters()
        await this.hbSizeMap()
        await this.removeBadBidSizes()

        if (this.config.services.geolocation.active) {
            await this.geoFilter(this.utils.window.OCM.country)
            await this.buildAdUnits()
        } else {
            await this.buildAdUnits()
        }

        this.setupAnalytics()

        this.configure()

        this.gptEventListeners()
    }

    configure() {
        let customGranularity = {
            "buckets": [
                {
                    "precision": 2,
                    "min": 0.01,
                    "max": 10,
                    "increment": 0.01
                },
                {
                    "precision": 2,
                    "max": 20,
                    "increment": 0.1
                },
                {
                    "precision": 2,
                    "max": 50,
                    "increment": 0.5
                },
            ]
        }

        this.utils.window.ocmpbjs.que.push(() => {
            this.utils.window.ocmpbjs.setConfig({
                cache: {
                    url: 'https://prebid.adnxs.com/pbc/v1/cache'
                },
                priceGranularity: (this.hb_config.custom_granularity) ? customGranularity : 'high',
                realTimeData: this.setupRealTimeData(),
                gptPreAuction: {
                    enabled: true
                },
                schain: this.config.schain,
                consentManagement: {
                    gdpr: {
                        cmpApi: 'iab',
                        timeout: (this.hb_config.bidderTimeout + 20000),
                        defaultGdprScope: true,
                        rules: [{
                            purpose: "storage",
                            enforcePurpose: true,
                            enforceVendor: true
                        }]
                    },
                    usp: {
                        cmpApi: 'iab',
                        timeout: 100 // US Privacy timeout 100ms
                    }
                },
                currency: {
                    "adServerCurrency": this.hb_config.adServerCurrency,
                    "conversionRateFile": "https://cdn.orangeclickmedia.com/tech/libs/latest_currencies.txt",
                },
                userSync: {
                    syncsPerBidder: 5,
                    syncDelay: 2500,
                    filterSettings: {
                        iframe: {
                            bidders: '*', // ['pubmatic', 'districtm', 'districtmDMX'], // '*' represents all bidders
                            filter: 'include'
                        }
                    },
                    //     userIds: [
                    //         {
                    //             name: "criteo"
                    //         },
                    //         {
                    //             name: "id5Id",
                    //             params: {
                    //                 partner: 347             // change to the Partner Number you received from ID5
                    //             },
                    //             storage: {
                    //                 type: "cookie",
                    //                 name: "id5id.1st",      // create a cookie with this name
                    //                 expires: 90,             // cookie lasts for 90 days
                    //                 refreshInSeconds: 8*3600 // refresh ID every 8 hours to ensure it's fresh
                    //             }
                    //         },
                    //         {
                    //             name: "pubCommonId",
                    //             storage: {
                    //                 type: "cookie",
                    //                 name: "_pubcid",         // create a cookie with this name
                    //                 expires: 365             // expires in 1 years
                    //             }
                    //         },
                    //
                    //     ]
                    // },
                },
                maxRequestsPerOrigin: 4,
                bidderTimeout: this.timeout,
                timeoutBuffer: 400,
                useBidCache: true,
                suppressEmptyKeys: true,
                enableSendAllBids: false,
                targetingControls: {
                    alwaysIncludeDeals: true,
                },
                pageUrl: this.utils.window.location.protocol + '//' + this.utils.window.location.hostname,
                ix: {
                    timeout: this.hb_config.bidderTimeout
                },
                rubicon: {
                    singleRequest: true
                }
            })

            const bidders = [
                { "bidder": "pubmatic", "domain": "pubmatic.com", "id": "157884" },
                { "bidder": "appnexus", "domain": "xandr.com", "id": "10452" },
                { "bidder": "adf", "domain": "adform.com", "id": "2656" },
                { "bidder": "openx", "domain": "openx.com", "id": "540861995" },
                { "bidder": "rubicon", "domain": "rubiconproject.com", "id": "20086" },
                { "bidder": "onetag", "domain": "onetag.com", "id": "5e5558fade26a9e" },
                { "bidder": "criteo", "domain": "criteo.com", "id": "B-060738" },
                { "bidder": "teads", "domain": "teads.tv", "id": "16578" },
                { "bidder": "adyoulike", "domain": "adyoulike.com", "id": "7ddaba905b34014f9bd3fe2c57d6d46e"},
                { "bidder": "gumgum", "domain": "gumgum.com", "id": "14064"},
                { "bidder": "medianet", "domain": "media.net", "id": "8CU32RQ72"},
                { "bidder": "triplelift", "domain": "triplelift.com", "id": "9262"},
                { "bidder": "unruly", "domain": "unrulygroup.com", "id": "6111915185540403805"},
                { "bidder": "yahoossp", "domain": "yahoo.com", "id": "59049"},
            ]

            bidders.forEach((entry) => {
                this.utils.window.ocmpbjs.setBidderConfig({
                    bidders: [entry.bidder],
                    "config": {
                        "schain": {
                            "validation": "relaxed",
                            "config": {
                                "ver":"1.0",
                                "complete": 1,
                                "nodes": [
                                    this.config.schain.config.nodes[0],
                                    {
                                        "asi":`${entry.domain}`,
                                        "sid":`${entry.id}`,
                                        "hp":1
                                    }
                                ]
                            }
                        }
                    }
                })
            })

            this.utils.window.ocmpbjs.bidderSettings = this.setupBidderSettings(this.hb_config.custom_granularity)

            if (this.analytics_adapters.length) {
                this.utils.window.ocmpbjs.enableAnalytics(this.analytics_adapters)
            }

            // Resize 1x1 bid impressions to actual size
            this.utils.window.ocmpbjs = this.utils.window.ocmpbjs || {que: []}
            this.utils.window.ocmpbjs.que.push(() => {
                this.utils.window.ocmpbjs.onEvent('bidWon', (bid) => {
                    if (bid.mediaType === 'banner') {
                        const slot = this.utils.window.googletag.pubads().getSlots()
                            .find(s => (s.getAdUnitPath() === bid.adUnitCode && s.getTargeting('hb_adid')[0] === bid.adId));
                        if (!slot) {
                            return
                        }

                        const safeFrame = this.utils.window.document.querySelector(`div[id^="google_ads_iframe_${slot.getSlotId().getId().replace(/(\.|\/)/g, '\\$1')}"]`);

                        if (safeFrame) {
                            safeFrame.style.height = `${bid.height}px !important`;
                            safeFrame.style.width = `${bid.width}px !important`;
                        }

                        // Stickies stuff
                        let slot_el = this.utils.window.document.getElementById(slot.getSlotElementId())
                        if (slot_el && slot_el.classList.contains('ocm-sticky-ad')) {
                            let ocm_sticky_content = slot_el.parentNode
                            let ocm_sticky_container = ocm_sticky_content.parentNode
                            let ocm_sticky = ocm_sticky_container.parentNode
                            if (ocm_sticky.classList.contains('left') || ocm_sticky.classList.contains('right')) {
                                let close_container = document.createElement('div')
                                close_container.classList.add('ocm-close-icon-container')

                                let close_span = document.createElement('span')
                                close_span.classList.add('ocm-close-icon')
                                close_span.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" enable-background="new 0 0 40 40">\n' +
                                    '                        <circle class="circle" cx="20" cy="20" r="19" stroke="#d07c00" stroke-width="2" stroke-linecap="round" stroke-miterlimit="10" fill="#1b1b1b"></circle>\n' +
                                    '                        <line x1="15" y1="15" x2="25" y2="25" stroke="#d07c00" stroke-width="2.5" stroke-linecap="round" stroke-miterlimit="10" fill="#d07c00"></line>\n' +
                                    '                        <line x1="25" y1="15" x2="15" y2="25" stroke="#d07c00" stroke-width="2.5" stroke-linecap="round" stroke-miterlimit="10"></line>\n' +
                                    '                        <path class="progress" d="M20 1c10.45 0 19 8.55 19 19s-8.55 19-19 19-19-8.55-19-19 8.55-19 19-19z" stroke="#1b1b1b" stroke-width="2.5" stroke-linecap="round" stroke-miterlimit="10" fill="#1b1b1b"></path>\n' +
                                    '                    </svg>'

                                ocm_sticky.style.width = `${bid.width}px !important`
                                ocm_sticky.style.height = `${bid.height}px !important`
                                ocm_sticky.style.marginTop = `-${bid.height / 2}px`
                                if (ocm_sticky.classList.contains('right')) {
                                    ocm_sticky_container.style.marginLeft = `-${bid.width}px`
                                }

                                ocm_sticky_content.style.width = `${bid.width}px !important`
                                ocm_sticky_content.style.height = `${bid.height}px !important`
                            }
                        }
                    }
                })
            })

            if (!this.mode.includes('lazyload')) {
                this.utils.window.ocmpbjs.addAdUnits(this.utils.window.OCM.ad_units)

                if (this.hb_config.debug) {
                    console.log(LOG_PREFIX + 'Added this.utils.window.OCM.ad_units to prebid', this.utils.window.ocmpbjs.adUnits)
                }

                if (this.mode === 'no_adserver') {
                    this.utils.window.ocmpbjs.requestBids({
                        bidsBackHandler: (bids, timeout, auctionId) => {
                            this.directRenderBids(bids)
                        }
                    })
                } else {
                    this.utils.window.ocmpbjs.requestBids({
                        bidsBackHandler: (bids, timeout, auctionId) => {
                            this.initAdServer([], false)
                        },
                    })
                }
            }
        })
    }

    initAdServer(slots, isLazyLoad = false) {
        if (isLazyLoad) {
            if (this.config.debug || this.hb_config.debug) {
                console.log(LOG_PREFIX + 'Calling initAdServer with slots:', slots)
            }

            let targetSlots = []
            for (const gas of slots) {
                let ad_element = this.utils.window.document.querySelector('[id="' + gas.getSlotElementId() + '"]')
                if (ad_element) {
                    ad_element.removeAttribute('data-lazyincluded-by-ocm')
                    ad_element.setAttribute('data-lazyloaded-by-ocm', '')
                    targetSlots.push(gas.getAdUnitPath())
                }
            }

            if (typeof slots !== 'undefined' && slots.length) {
                this.utils.window.googletag.cmd.push(() => {
                    this.utils.window.ocmpbjs.que.push(() => {
                        try {
                            this.utils.window.ocmpbjs.setTargetingForGPTAsync(targetSlots)

                            this.utils.window.OCM.pb_done = true

                            if (this.config.services.uam.active) {
                                if (this.utils.window.OCM.uam_done) {
                                    googletag.pubads().refresh(slots)
                                } else {
                                    setTimeout(() => {
                                        googletag.pubads().refresh(slots)
                                    }, 400);
                                }
                            } else {
                                googletag.pubads().refresh(slots)
                            }
                        } catch(error) {
                            console.error(LOG_PREFIX, error)
                        }
                    })
                })
            }
        } else {
            if (this.config.debug || this.hb_config.debug) {
                console.log(LOG_PREFIX + 'Calling initAdServer for all slots')
            }

            this.utils.window.googletag.cmd.push(() => {
                this.utils.window.ocmpbjs.que.push(() => {
                    this.utils.window.ocmpbjs.setTargetingForGPTAsync()
                    this.utils.window.OCM.pb_done = true

                    if (this.config.services.uam.active) {
                        if (this.utils.window.OCM.uam_done) {
                            googletag.pubads().refresh()
                            this.utils.window.OCM.init_ad_server_set = true
                        } else {
                            setTimeout(() => {
                                googletag.pubads().refresh()
                                this.utils.window.OCM.init_ad_server_set = true
                            }, 400);
                        }
                    } else {
                        googletag.pubads().refresh()
                        this.utils.window.OCM.init_ad_server_set = true
                    }
                })
            })
        }
    }

    // async filterAdUnitsByDataAttribute() {
    //     if (this.config.debug || this.hb_config.debug) {
    //         console.log(LOG_PREFIX + 'Filtering HB ad units by data-oaha...')
    //     }
    //
    //     // If oaha (=ad unit code) is defined, filter out the rest of the ad units
    //     let oaha = this.utils.getDataAttributeValue('oaha')
    //     if (oaha) {
    //         this.hb_config.adUnits = this.hb_config.adUnits.filter((adunit) => {
    //             if (adunit.code === oaha) {
    //                 return true
    //             }
    //
    //             if (this.config.debug || this.hb_config.debug) {
    //                 console.log(LOG_PREFIX + 'Removing ' + this.hb_config.adUnits[i].code + ' due to data-oaha attribute')
    //             }
    //             return false
    //         })
    //     }
    // }

    async filterAdUnitsByPageType() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Filtering HB ad units by page type...')
        }

        let done = 0

        // Filter out this.hb_config.adUnits according to page type
        this.hb_config.adUnits = this.hb_config.adUnits.filter((adunit) => {
            if (this.utils.allowPageType(adunit.page_types)) {
                return true
            }

            if (this.config.debug || this.hb_config.debug) {
                console.log(LOG_PREFIX + 'Removing ' + adunit.code + ' due to page type')
            }
            done++
            return false
        })

        if (done && (this.config.debug || this.hb_config.debug)) {
            console.log(LOG_PREFIX + 'Done filtering HB ad units by page type (removed ' + done + ' ad units) ***')
        }
    }

    async filterAdUnitsByDevice() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Filtering HB ad units by device...')
        }

        // Remove ad units that should not exist in mobile or desktop
        this.hb_config.adUnits = this.hb_config.adUnits.filter((adunit) => {
            if (this.utils.is_mobile) {
                if (adunit.visibility.hasOwnProperty('mobile') && !adunit.visibility.mobile) {
                    if (this.config.debug || this.hb_config.debug) {
                        console.log(LOG_PREFIX + 'Removing' + adunit.code + ' due to hidden in mobile')
                    }

                    return false
                }
            } else {
                if (adunit.visibility.hasOwnProperty('desktop') && !adunit.visibility.desktop) {
                    if (this.config.debug || this.hb_config.debug) {
                        console.log(LOG_PREFIX + 'Removing' + adunit.code + ' due to hidden in desktop')
                    }

                    return false
                }
            }

            return true
        })
    }

    async jsonifyAds() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'JSONifying HB ads...')
        }

        this.hb_config.adUnits = this.hb_config.adUnits.map((adunit) => {
            // Transform sizes to arrays
            if (typeof adunit.sizes !== 'undefined') {
                if (!adunit.sizes.startsWith('[[')) {
                    adunit.sizes = '[' + adunit.sizes + ']'
                }

                if (this.utils.hasJsonStructure(adunit.sizes)) {
                    adunit.sizes = JSON.parse(adunit.sizes)
                }
            }

            if (adunit.hasOwnProperty('size_mapping')) {
                adunit.size_mapping = adunit.size_mapping.map((sm) => {
                    if (this.utils.hasJsonStructure(sm.ad_sizes)) {
                        sm.ad_sizes = JSON.parse(sm.ad_sizes)
                    }

                    if (this.utils.hasJsonStructure(sm.window_size)) {
                        sm.window_size = JSON.parse(sm.window_size)
                    }

                    return sm
                })
            }

            // Transform string arrays to real arrays/objects if key size or outstream_options exists
            adunit.bids = adunit.bids.map((bid) => {
                //Loop params of each bid and convert to real json object if it has json structure
                for (let k = 0; k < Object.keys(bid.params).length; k++) {
                    let ocm_param_key = Object.keys(bid.params)[k]
                    let ocm_param = bid.params[ocm_param_key]
                    if (ocm_param_key === 'size' && ocm_param !== null && ocm_param.indexOf('[[') < 0 && bid.bidder !== 'ix') {
                        ocm_param = '[' + ocm_param + ']';
                    }

                    if (bid.bidder === 'engageya') {
                        bid.params.pageUrl = String(this.utils.window.location.href)
                    }

                    if (this.utils.hasJsonStructure(ocm_param)) {
                        bid.params[ocm_param_key] = JSON.parse(ocm_param)
                    }
                }

                return bid
            })

            return adunit
        })
    }

    async removeNullBidParameters() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Removing null bid parameters...')
        }

        for (let adUnit of this.hb_config.adUnits) {
            for (let bid of adUnit.bids) {
                for (let param in bid.params) {
                    if (bid.params[param] === null) {
                        delete bid.params[param]
                    }
                }
            }
        }

        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Just removed null bid params', this.hb_config.adUnits)
        }
    }

    async hbSizeMap() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Applying size mapping on HB ads...')
        }

        this.hb_config.adUnits = this.hb_config.adUnits.map((adunit) => {
            if (typeof adunit.size_mapping !== 'undefined' && adunit.size_mapping.length) {
                // Perform proper size mapping on the ad units
                let size_mapping = adunit.size_mapping
                for (let sm = (size_mapping.length - 1); sm >= 0; sm--) {
                    if (this.utils.screen_width >= size_mapping[sm].window_size[0]) {
                        adunit.sizes = size_mapping[sm].ad_sizes
                        break
                    }

                    if ((sm-1) >= 0) {
                        if (this.utils.screen_width < size_mapping[sm].window_size[0] && this.utils.screen_width >= size_mapping[(sm - 1)].window_size[0]) {
                            adunit.sizes = size_mapping[(sm - 1)].ad_sizes
                            return adunit
                        }
                    } else {
                        if (this.utils.screen_width >= size_mapping[sm].window_size[0]) {
                            adunit.sizes = size_mapping[sm].ad_sizes
                            return adunit
                        }
                    }
                }

                // If no sizes were specified, remove the ad unit from going into the pbjs config
                if (adunit.sizes.length === 0) {
                    if (this.config.debug || this.hb_config.debug) {
                        console.log(LOG_PREFIX + 'Removing ' + adunit.code + ' due to empty size mapping for this window size')
                    }

                    adunit = null
                }
            } else {
                // Old school
                if (adunit) {
                    // Filter ad unit sizes depending on screen width
                    let sizes
                    if (this.utils.hasJsonStructure(adunit.sizes)) {
                        sizes = JSON.parse(adunit.sizes)
                    } else {
                        sizes = adunit.sizes
                    }

                    let has_bigger_than_728 = false

                    // Check if ad_unit has > 728 sizes
                    for (let i = 0; i < sizes.length; i++) {
                        if (sizes[i][0] > 728) {
                            has_bigger_than_728 = true
                        }
                    }

                    // Remove all sizes larger than the screen width
                    for (let k = 0; k < sizes.length; k++) {
                        if (sizes[k][0] > this.utils.screen_width) {
                            if (this.config.debug || this.hb_config.debug) {
                                console.log(LOG_PREFIX + 'Removing ' + sizes[k][0] + ' from' + adunit.code + ' due to screen width')
                            }
                            sizes.splice(k, 1)
                            k--
                        }
                    }

                    // Remove all sizes below 728 if has_bigger_than 728 and screen width is < 768
                    for (let j = 0; j < sizes.length; j++) {
                        if (this.utils.screen_width >= 768 && sizes[j][0] < 728 && has_bigger_than_728) {
                            sizes.splice(j, 1)
                            j--
                        }
                    }

                    adunit.sizes = JSON.parse(JSON.stringify(sizes))
                }
            }

            return adunit
        })

        // Filter out null ad units
        this.hb_config.adUnits = this.hb_config.adUnits.filter((adunit) => {
            return (adunit !== null)
        })
    }

    async removeBadBidSizes() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Removing bad bid sizes from HB ads...')
        }
        let keep_bid_sizes = []

        this.hb_config.adUnits = this.hb_config.adUnits.map((adunit) => {
            // Transform string arrays to real arrays/objects if key size or outstream_options exists
            adunit.bids = adunit.bids.filter((bid) => {
                keep_bid_sizes = []
                // bid size params
                if (typeof bid.params.size !== "undefined") {
                    if (!Array.isArray(bid.params.size[0]) && adunit.sizes.containsArray(bid.params.size)) {
                        keep_bid_sizes.push(bid.params.size)
                    } else if (Array.isArray(bid.params.size[0])) {
                        keep_bid_sizes = bid.params.size.filter((size) => {
                            return adunit.sizes.containsArray(size)
                        });
                    }

                    if (keep_bid_sizes.length) {
                        // Keep only proper bid sizes
                        if (bid.bidder === 'ix') {
                            // ix needs [x, y] (array of ints) in the bid request and always has 1 array in the size parm
                            bid.params.size = JSON.parse(JSON.stringify(keep_bid_sizes[0]))
                        } else {
                            // for the rest, size is obsolete for the bid request
                            bid.params.size = JSON.parse(JSON.stringify(keep_bid_sizes))
                        }
                    } else {
                        // Remove this bidder from the ad unit since its size should not be used
                        if (this.config.debug || this.hb_config.debug) {
                            console.log(LOG_PREFIX + 'Removing ', bid, ' due to ad unit sizes')
                        }

                        return false
                    }
                }

                return true
            })

            return adunit
        })
    }

    geoFilter(country) {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Geo-filtering HB ads...')
        }

        let remove_bidders = []

        for (const bidder of this.hb_config.bidders) {
            if (bidder.geo.length > 0 && bidder.geo.includes(country)) {
                if (this.config.debug || this.hb_config.debug) {
                    console.log(LOG_PREFIX + 'Geo-filtering ' + bidder.name + ' (country ' + country +')')
                }
                remove_bidders.push(bidder.name)
            }
        }

        this.hb_config.adUnits = this.hb_config.adUnits.map((adunit) => {
            let bids = []

            for (const bid of adunit.bids) {
                if (!remove_bidders.includes(bid.bidder)) {
                    bids.push(bid)
                }
            }

            adunit.bids = JSON.parse(JSON.stringify(bids))

            return adunit
        })
    }

    async buildAdUnits() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Building final HB ads...')
        }

        this.utils.window.OCM.ad_units = this.hb_config.adUnits.map((adunit) => {
            let media_types = {}

            if (typeof adunit.media_types !== 'undefined') {
                if (adunit.media_types.includes('banner')) {
                    media_types.banner = {
                        sizes: (typeof adunit.sizes === 'string') ? JSON.parse(adunit.sizes) : adunit.sizes
                    }
                }

                if (adunit.media_types.indexOf('native') > -1) {
                    media_types.native = adunit.native
                    if (this.utils.hasJsonStructure(media_types.native.image.sizes)) {
                        media_types.native.image.sizes = JSON.parse(media_types.native.image.sizes)
                    }
                }

                if (adunit.media_types.indexOf('video') > -1) {
                    adunit.video.mimes = ['video/mp4','video/x-flv', 'video/ogg', 'video-webm']
                    media_types.video = adunit.video
                    media_types.video.pos = 0
                    media_types.video.maxduration = 180
                    media_types.video.protocols = [1,2,3,4,5,6,7,8]
                    media_types.video.api = [1,2,3,4,5,6]
                    media_types.video.skip = 1
                    media_types.video.placement = 3
                    media_types.video.playbackmethod = [1,2,4,5,6]
                    if (this.utils.hasJsonStructure(media_types.video.playerSize)) {
                        media_types.video.playerSize = JSON.parse(media_types.video.playerSize)
                    }

                    media_types.video.renderer = {
                        url: '//cdn.orangeclickmedia.com/tech/libs/player.js',
                        backupOnly: true,
                        render: (bid) => {
                            try {
                                let aspectRatio = "16:9"
                                if (bid.hasOwnProperty('width') && bid.hasOwnProperty('height') && bid.width > 1 && bid.height > 1) {
                                    aspectRatio = (bid?.width > bid?.height) ? "16:9" : "9:16"
                                }

                                let adDiv = this.utils.window.document.querySelector('[data-oau-code="' + bid.adUnitCode + '"]')
                                if (!adDiv) {
                                    console.error('Could not find element to render outstream ad', bid)
                                    return
                                }

                                // OpenX case
                                if (bid.hasOwnProperty('vastUrl') && !bid.hasOwnProperty('vastXml') && !bid.hasOwnProperty('ad')) {
                                    console.warn('Bid lacking vastXml and ad properties but included vastUrl. Trying to fetch vastXml from vastUrl.', bid)
                                    let xhr = new XMLHttpRequest()
                                    xhr.open('GET', bid.vastUrl, true)
                                    xhr.responseType = 'document'
                                    xhr.overrideMimeType('text/xml')
                                    xhr.onload = () => {
                                        if (xhr.readyState === xhr.DONE) {
                                            if (xhr.status === 200) {
                                                let serializer = new XMLSerializer()
                                                bid.vastXml = serializer.serializeToString(xhr.responseXML.documentElement)
                                                let adResponse = {
                                                    ad: {
                                                        video: {
                                                            content: bid.vastXml
                                                        }
                                                    }
                                                }
                                                // push to render queue because ANOutstreamVideo may not be loaded yet.
                                                bid.renderer.push(() => {
                                                    this.buildRenderer(adDiv.id, adResponse, aspectRatio, media_types.video)
                                                })
                                            }
                                        }
                                    }
                                    xhr.send()
                                } else {
                                    let adResponse = {
                                        ad: {
                                            video: {
                                                content: bid.vastXml
                                            }
                                        }
                                    }
                                    // push to render queue because ANOutstreamVideo may not be loaded yet.
                                    bid.renderer.push(() => {
                                        this.buildRenderer(adDiv.id, adResponse, aspectRatio, media_types.video)
                                    })
                                }

                                // Remove div#google_ads_iframe
                                let googleAdDiv = adDiv.querySelector('div[id^="google_ads_iframe_"]')
                                if (googleAdDiv) {
                                    googleAdDiv.remove()
                                }
                            } catch (e) {
                                console.error(e)
                                console.error("Error in ad rendering!")
                            }
                        }
                    }
                }
            } else {
                media_types.banner = {
                    sizes: (typeof adunit.sizes === 'string') ? JSON.parse(adunit.sizes) : adunit.sizes
                }
            }

            adunit.mediaTypes = media_types

            return adunit
        })


        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Built HB ad units', this.utils.window.OCM.ad_units)
        }
    }

    buildRenderer(adDiv, adResponse, aspectRatio, cfg) {
        this.utils.window.ANOutstreamVideo.renderAd({
            targetId: adDiv, // target div id to render video.
            adResponse: adResponse,
            rendererOptions: {
                adText: "inRead by OCM",
                aspectRatio: aspectRatio,
                showMute: true,
                showVolume: true,
                showProgressBar: true,
                showPlayToggle: false,
                allowFullscreen: false,
                disableCollapse: {
                    enabled: true,
                    replay: true,
                    hideControlBar: true
                },
                nonViewableBehavior: "mute",
                playVideoVisibleThreshold: 0,
                skippable: {
                    enabled: false
                },
                learnMore: {
                    enabled: true,
                    clickToPause: true
                },
                playerSkin: {
                    topBarColor: '#000',
                    topBarTextColor: '#e8a63d',
                    dividerColor: 'transparent',
                    dividerHeight: 0,
                    bigPlayButtonColor: "#e8a63d",
                },
                sidestream: cfg.sidestream
            }
        })
    }

    loadRivr() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Loading Rivr analytics library...')
        }

        // Load the Rivr addon library
        (function () {
            let pbs = document.createElement("script")
            pbs.type = "application/javascript"
            pbs.src = '//ocean.rivrai.com/rivraddon.js'
            let node = document.getElementsByTagName('script')[0]
            node.parentNode.insertBefore(pbs, node)

            ocmTimeIt("to load rivraddon.js")
        })()
    }

    /**
     * IndexExchange's library solution for HB user syncing
     */
    loadRti() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Loading IX\'s RTI  library...')
        }

        // RTI Library
        // Load the RTI script
        let d = document
        let pbs = d.createElement("script")
        pbs.src = '//js-sec.indexww.com/ht/p/188610-14764350600421.js'
        let node = document.getElementsByTagName('script')[0]
        node.parentNode.insertBefore(pbs, node)

        ocmTimeIt("to load the rti library")

        // Supply RTI data to bidders
        let headertag = headertag || {}
        headertag.cmd = headertag.cmd || []
        headertag.cmd.push(function() {
            let rtiData = headertag.getIdentityInfo()

            let responsePending = 0
            for (let rtiPartner in rtiData) {
                if (rtiData.hasOwnProperty(rtiPartner) && rtiData[rtiPartner].responsePending === true) {
                    responsePending = 1
                    break
                }
            }

            // Subscribe if response is pending
            if (responsePending) {
                this.utils.window.headertag.subscribeEvent('rti_partner_request_complete', false, function(eventName, eventData) {
                    rtiData = headertag.getIdentityInfo()
                })
            }
        })
        // -- RTI Library
    }

    /**
     * Pubmatic's ID Hub solution for HB user syncing
     */
    setupIdHub() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Loading Pubmatic\'s ID Hub library...')
        }

        let purl = this.utils.window.location.href
        let url = '//ads.pubmatic.com/AdServer/js/pwt/157884/2162'
        let profileVersionId = ''
        if (purl.indexOf('pwtv=') > 0) {
            let regexp = /pwtv=(.*?)(&|$)/g
            let matches = regexp.exec(purl)
            if (matches.length >= 2 && matches[1].length > 0) {
                profileVersionId = '/' + matches[1]
            }
        }
        let wtads = document.createElement('script')
        wtads.async = true
        wtads.type = 'text/javascript'
        wtads.src = url + profileVersionId + '/pwt.js'
        let node = document.getElementsByTagName('script')[0]
        node.parentNode.insertBefore(wtads, node)
    }

    /**
     * Adjusts bidderTimeout if device is mobile, depending on hb mode
     * @param isLazyLoad
     */
    adjustBidderTimeout(isLazyLoad) {
        //timeout to control how long bidders have to respond.
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'bidderTimeout = ' + this.hb_config.bidderTimeout)
        }

        // increase OCM_PREBID_TIMEOUT by 100% if the device is mobile
        if (this.utils.is_mobile) {
            if (isLazyLoad) {
                this.hb_config.bidderTimeout += (this.hb_config.bidderTimeout * .5)
            } else {
                this.hb_config.bidderTimeout += this.hb_config.bidderTimeout
            }

            if (this.config.debug || this.hb_config.debug) {
                console.log(LOG_PREFIX + 'Mobile device, so bidderTimeout = ', this.hb_config.bidderTimeout)
            }
        }
    }

    /**
     * Load the Prebid Javascript Library Async. We recommend loading it immediately after
     * the initAdserver() and setTimeout functions.
     */
    loadPrebidLib() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Loading OCM prebid library...')
        }

        let pbs = document.createElement("script")
        pbs.type = "text/javascript"
        pbs.async = true
        pbs.src = 'https://cdn.orangeclickmedia.com/tech/libs/ocmpb.js' //6.4.0

        let node = document.getElementsByTagName('script')[0]
        node.parentNode.insertBefore(pbs, node)
    }

    gptEventListeners() {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Setting up GPT events for HB')
        }
        // Hide GPT iframe when HB video was loaded
        googletag.cmd.push(() => {
            googletag.pubads().addEventListener('slotRenderEnded', (event) => {
                // inread_pb passback call
                if (event.slot.getAdUnitPath().indexOf('inread_pb') > -1 && event.isEmpty) {
                    // Call our global oopb()
                    if (typeof this.utils.window.oopb !== "undefined") {
                        this.utils.window.oopb()
                    }
                }
            })
        })
    }

    setupRealTimeData() {
        // return (this.config.domain === 'pronews.gr') ? {
        //     dataProviders: [{
        //         name: "browsi",
        //         params: {
        //             url: "yield-manager.browsiprod.com",
        //             siteKey: this.config.domain.split('.')[0],
        //             pubKey: "orangeclickmedia",
        //             keyName: "browsiViewability"
        //         }
        //     }]
        // } :
        return {
            dataProviders: [{}]
        }
    }

    setupAnalytics() {
        if (typeof this.hb_config.analytics !== 'undefined') {
            if (this.hb_config.analytics.indexOf('ocmpba') >= 0) {
                this.analytics_adapters.push({provider: 'ocmpba'})
            }
            if (this.hb_config.analytics.indexOf('ga') >= 0) {
                this.analytics_adapters.push({provider: 'ga', options: {enableDistribution: false}})
            }
            if (this.hb_config.analytics.indexOf('roxot') >= 0) {
                this.analytics_adapters.push({
                    provider: 'roxot',
                    options: {publisherIds: ["6e522d36-dae0-45bd-9d3c-de88ef4334ee"]}
                })
            }

            // if (this.hb_config.analytics.indexOf('rivr') > -1) {
            //     ocmpbjs.que.push(() => {
            //         let RIVR_CLIENT_ID = "ocm"
            //         let RIVR_CLIENT_AUTH_TOKEN = "b2NtOlJpPUdBUEMvOEJKcUQ1JA=="
            //
            //         let args = {
            //             clientID: RIVR_CLIENT_ID,
            //             authToken: RIVR_CLIENT_AUTH_TOKEN,
            //             siteCategories: [],
            //             pbjs: window.ocmpbjs,
            //         }
            //
            //         if (window.rivraddon) {
            //             rivraddon.enable(args)
            //         } else {
            //             window.RIVR_ARGS = args
            //         }
            //     })
            // }
        }
    }

    setupBidderSettings(hasCustomGranularity) {
        if (this.config.debug || this.hb_config.debug) {
            console.log(LOG_PREFIX + 'Setting up adserverTargeting & bidder settings')
        }

        let bidderSettings = {
            standard: {
                suppressEmptyKeys: true,
                adserverTargeting: [
                    {
                        key: "infi_article_count",
                        val: (bidResponse) => {
                            return this.utils.window.OCM?.infi_article_count.toString()
                        }
                    },
                    {
                        key: "hb_bidder",
                        val: (bidResponse) => {
                            return bidResponse.bidderCode
                        }
                    }, {
                        key: "hb_adid",
                        val: (bidResponse) => {
                            return bidResponse.adId
                        }
                    }, {
                        key: "hb_pb",
                        val: (bidResponse) => {
                            return (hasCustomGranularity) ? bidResponse.pbCg : bidResponse.pbHg
                        },
                    }, {
                        key: "hb_size",
                        val: (bidResponse) => {
                            return bidResponse.getSize()
                        }
                    }, {
                        key: "hb_format",
                        val: (bidResponse) => {
                            return bidResponse.mediaType
                        }
                    }, {
                        key: "hb_cpm",
                        val: (bidResponse) => {
                            return bidResponse.cpm
                        }
                    }, {
                        key: 'hb_deal',
                        val: (bidResponse) => {
                            return bidResponse.dealId
                        }
                    }, {
                        key: 'hb_source',
                        val: (bidResponse) => {
                            return bidResponse.source
                        }
                    }, {
                        key: 'hb_sf',
                        val: (bidResponse) => {
                            let nsf_bidders = ['justpremium', 'teads', 'gumgum', 'triplelift', 'adhash', 'engageya']
                            let is_video = (bidResponse.mediaType === 'video')
                            let is_tnc_openx = false
                            if (['sportdog.gr', 'startup.gr', 'provocateur.gr', 'newpost.gr'].includes(this.config.domain)) {
                                is_tnc_openx = (bidResponse.bidderCode === 'openx' &&
                                    ['/15637987/sportdog.gr/inline1',
                                        '/21748129265/startup.gr/billboard1',
                                        '/15637987/provoc_right_1',
                                        '/15637987/newpost_right_1'].includes(bidResponse.adUnitCode))
                            }
                            return (is_tnc_openx || is_video || nsf_bidders.includes(bidResponse.bidderCode)) ? 'false' : 'true';
                        }
                    }, {
                        key: 'hb_cache_id',
                        val: (bidResponse) => {
                            return bidResponse.videoCacheKey
                        }
                    }, {
                        key: 'hb_uuid',
                        val: (bidResponse) => {
                            return bidResponse.videoCacheKey
                        }
                    }
                ]
            }
        }

        let bidder
        let tmp_bid_cpm_adjust = 1.0
        let ocm_bidder_name = ''
        // Loop through all bidders and push the configuration to the bidderSettings
        if (typeof this.hb_config.bidders !== 'undefined') {
            for (bidder = 0; bidder < this.hb_config.bidders.length; bidder++) {
                tmp_bid_cpm_adjust = parseFloat(this.hb_config.bidders[bidder].bid_cpm_adjust)
                ocm_bidder_name = this.hb_config.bidders[bidder].name
                bidderSettings[this.hb_config.bidders[bidder].name] = {
                    bidCpmAdjustment: (bidCpm, bid, ocm_bidder_name) => {
                        switch (ocm_bidder_name) {
                            case 'smartadserver':
                                if (bid.netRevenue === true) {
                                    tmp_bid_cpm_adjust = 1
                                } else {
                                    if (bid.dealId !== "") {
                                        tmp_bid_cpm_adjust = 0.9
                                    }
                                }
                                break
                            case 'improvedigital':
                                if (bid.isNet === true) {
                                    tmp_bid_cpm_adjust = 1
                                } else {
                                    if (bid.dealId !== "") {
                                        tmp_bid_cpm_adjust = 0.9
                                    }
                                }
                                break
                            default:
                                tmp_bid_cpm_adjust = 1.0
                                break
                        }

                        return bidCpm * tmp_bid_cpm_adjust
                    }
                }
            }
        }

        return bidderSettings
    }

    directRenderBids(bidResponses) {
        if (typeof bidResponses === 'undefined' || !bidResponses.length) {
            return
        }

        for (let divId in bidResponses) {
            if (this.config.debug || this.hb_config.debug) {
                console.log(LOG_PREFIX + 'Found bid for #' + divId)
            }

            let ad_div = document.getElementById(divId)

            if (ad_div) {
                if (this.config.debug || this.hb_config.debug) {
                    console.log(LOG_PREFIX + 'Ad div = ', ad_div)
                }

                let frmEl = document.createElement('iframe')
                frmEl.setAttribute('id', divId + '_ocm_hb')
                frmEl.style.border = '0'
                frmEl.style.overflow = 'hidden'
                frmEl.style.margin = '0'
                frmEl.TOPMARGIN = '0'
                frmEl.LEFTMARGIN = '0'
                frmEl.ALLOWTRANSPARENCY = 'true'
                frmEl.width = '0'
                frmEl.height = '0'

                ad_div.appendChild(frmEl)

                    if (this.config.debug || this.hb_config.debug) {
                        console.log(LOG_PREFIX + 'Appended frmEl to ad_div', ad_div)
                    }

                    let iframeDoc = frmEl.contentWindow.document
                    let adServerTargeting = ocmpbjs.getAdserverTargetingForAdUnitCode(divId)
                    if (this.config.debug || this.hb_config.debug) {
                        console.log(LOG_PREFIX + 'adServerTargeting', adServerTargeting)
                    }

                    // If any bidders return any creatives
                    if (adServerTargeting && adServerTargeting['hb_adid']) {
                        if (this.config.debug || this.hb_config.debug) {
                            console.log(LOG_PREFIX + 'Rendering ad for ' + divId)
                        }
                        ocmpbjs.renderAd(iframeDoc, adServerTargeting['hb_adid'])
                        ad_div.querySelector('script').remove()
                    ad_div.querySelector('div[id^="google_ads_iframe_"]').remove()
                    ad_div.style.display = 'block'
                }
            } else {
                if (this.config.debug || this.hb_config.debug) {
                    console.warn(LOG_PREFIX + 'Ad div #' + divId + ' not found')
                }
            }
        }
    }
}
